import React from 'react';
import styled, { css } from 'styled-components';
import TextareaAutosize from 'react-textarea-autosize';
import { FaTimes } from 'react-icons/fa';

import PropTypes from '+/instances/propTypesInstance';
import Theme from '+/instances/Theme';
import themeVars, { fromProps, ifProp } from '+/utils/themeVars';

import StyledRow from '../StyledRow';

export const StyledInput = styled.input`
  height: ${themeVars('inputHeight')};
  margin-top: 0.5em;
  padding: ${themeVars('inputPadding')};
  width: ${ifProp({ half: '45%', default: '100%' })};

  background: ${themeVars('inputBackgroundColor')};
  border: 1px solid ${themeVars('inputBorderColor')};
  border-radius: ${themeVars('inputRadius')};
  color: ${themeVars('inputColor')};

  font-size: ${themeVars('inputFontSize')};
  line-height: 1;

  transition: border-color 0.2s ease-out;

  pointer-events: ${ifProp({ disabled: 'none' })};

  &::placeholder {
    color: ${themeVars('inputPlaceholderColor')};
  }

  ${ifProp({
    error: css`
      border-color: ${themeVars('inputBorderColorError')};
    `,
  })}
`;

const StyledClearXContainer = styled.button`
  display: inline-flex;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);

  background: none;
  border: none;

  cursor: pointer;

  &:hover {
    color: red;
  }
`;

export const StyledClearX = props => (
  <StyledClearXContainer {...props}>
    <FaTimes />
  </StyledClearXContainer>
);

export const StyledToggleButton = styled.div`
  display: flex;
  justify-content: ${ifProp({ center: 'center', default: 'space-between' })};
  margin: 0.5em;
  padding: 0.5em;
  width: 6.5em;

  border: 2px solid ${fromProps('color')};

  -webkit-text-stroke-width: 1px;
  -webkit-text-stroke-color: rgba(0, 0, 0, 0.1);
  color: ${themeVars('colorWhite')};
  font-size: 1.2em;
  font-weight: bold;

  cursor: pointer;

  svg {
    color: ${fromProps('color')};
  }

  ${ifProp(
    'off',
    css`
      border-color: ${themeVars('colorDisabledGrey')};
      background: repeating-linear-gradient(
        45deg,
        ${themeVars('colorWhite')},
        ${themeVars('colorWhite')} 10px,
        ${themeVars('colorDisabledLightGrey')} 10px,
        ${themeVars('colorDisabledLightGrey')} 20px
      );

      color: ${themeVars('colorDisabledGrey')};

      svg {
        color: ${themeVars('colorDisabledGrey')};
      }
    `,
  )}
`;

export const StyledIconToggle = styled.button`
  height: ${props => (props.exactHeight ? props.exactHeight : 'auto')};
  margin: 0.15em;
  width: ${props => (props.exactWidth ? props.exactWidth : 'auto')};
  background: ${props => props.background || 'auto'};
  color: ${ifProp({
    active: themeVars('colorPrimary'),
    default: themeVars('textColorDefault'),
  })};

  svg {
    height: 100%;
    width: 100%;

    path,
    g {
      fill: ${ifProp({
        active: themeVars('colorPrimary'),
        default: themeVars('textColorDefault'),
      })} !important;
      stroke: ${ifProp({
        active: themeVars('colorPrimary'),
        default: themeVars('textColorDefault'),
      })} !important;
    }
  }
  cursor: pointer;
`;

const SelectWrapper = styled.div`
  margin-top: ${ifProp({ default: '0.5em', noMargin: '0' })};
  position: relative;

  cursor: pointer;

  &::after {
    height: 14px;
    position: absolute;
    right: 18px;
    top: 50%;
    transform: translateY(-50%);
    width: 13px;

    background: ${props =>
      `url('data:image/svg+xml;utf8,<svg aria-hidden="true" focusable="false" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="${themeVars(
        'textColorDefault',
      )(props).replace('#', '%23')}" d="M143 352.3L7 216.3c-9.4-9.4-9.4-24.6 0-33.9l22.6-22.6c9.4-9.4 24.6-9.4
    33.9 0l96.4 96.4 96.4-96.4c9.4-9.4 24.6-9.4 33.9 0l22.6 22.6c9.4 9.4 9.4 24.6
    0 33.9l-136 136c-9.2 9.4-24.4 9.4-33.8 0z"></path></svg>');`};
    background-size: 100% auto;
    background-position: center center;
    background-repeat: no-repeat;

    pointer-events: none;

    content: '';
  }
`;

const StyledSelectElement = styled(StyledInput).attrs({ as: 'select' })`
  appearance: none;
  min-width: 12em;
  margin-top: 0;
  padding-bottom: 0;
  padding-top: 0;
  line-height: ${themeVars('inputHeight')};
`;

export const StyledSelect = ({ children, noMargin = false, ...otherProps }) => (
  <Theme light>
    <SelectWrapper noMargin={noMargin}>
      <StyledSelectElement {...otherProps}>{children}</StyledSelectElement>
    </SelectWrapper>
  </Theme>
);

StyledSelect.propTypes = {
  children: PropTypes.node.isRequired,
  noMargin: PropTypes.bool,
};

export const StyledOption = styled.option`
  ${'fill in'}
`;

export const StyledLabel = styled.label`
  display: block;
  margin-bottom: 0.65em;
  margin-top: 0.65em;
  position: relative;
  width: ${ifProp('minWidth', 'auto', '100%')};

  color: ${ifProp({
    error: themeVars('labelColorError'),
    default: themeVars('textColorDefault'),
  })};

  font-family: ${ifProp({
    formal: themeVars('fontFormal'),
    default: themeVars('fontLabels'),
  })};
  font-size: ${themeVars('labelFontSize')};

  ${ifProp({
    noMargins: css`
      margin: 0;
    `,
  })}

  ${ifProp({
    smallFont: css`
      font-size: 0.75rem;
    `,
    inline: css`
      font-size: ${themeVars('pFontSize')};
    `,
  })}
`;

export const StyledErrorMessage = styled.div`
  height: 0.75rem;
  margin: 0.5rem 0 -0.25rem;

  color: ${themeVars('colorRed')};
  font-size: 0.75em;
  text-align: ${ifProp('center', 'center')};
`;

export const StyledNote = styled.div`
  margin-top: 0.5rem;

  font-size: 0.75em;
  opacity: 0.67;
  text-align: ${ifProp('center', 'center')};
`;

export const StyledTextArea = styled(StyledInput).attrs({ as: TextareaAutosize })`
  resize: vertical;
  height: auto;
  line-height: 1.6;
`;

const StyledBox = styled.span`
  display: block;
  height: 1.25rem;
  left: 0;
  position: absolute;
  top: 0.2rem;
  width: 1.25rem;

  background: ${ifProp({
    decorative: themeVars('colorActionIcon'),
    default: themeVars('colorDarkPrimary'),
  })};
  border-color: ${ifProp({
    decorative: themeVars('colorActionIcon'),
    default: themeVars('colorDarkPrimary'),
  })};

  cursor: pointer;

  transition: background-color 0.2s ease 0s, border-color 0.2s ease 0s;

  ${ifProp({
    danger: css`
      background: ${themeVars('colorDanger')};
      border-color: ${themeVars('colorDanger')};
    `,
  })}

  svg {
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translate(50%, -50%);
    width: 0.7rem;
    height: 0.6875rem;
    fill: ${ifProp({
      decorative: themeVars('colorDarkPrimary'),
      default: themeVars('colorWhite'),
    })};
  }

  ${ifProp({
    danger: css`
      background: ${themeVars('colorDanger')};
      border-color: ${themeVars('colorDanger')};
    `,
  })}
`;

const StyledToggle = styled.span`
  display: inline-block;
  height: 1.3125em;
  margin-left: 0.75rem;
  position: relative;
  vertical-align: middle;
  width: 2.5em;

  background: ${themeVars('colorDarkPrimary')};
  border: 2px solid ${themeVars('colorDarkPrimary')};
  border-radius: 0.65625em;

  cursor: pointer;

  transition: background-color 0.2s ease 0s, border-color 0.2s ease 0s;

  ${ifProp({
    danger: css`
      background: ${themeVars('colorDanger')};
      border-color: ${themeVars('colorDanger')};
    `,
  })}

  &::before {
    height: 1.0625em;
    position: absolute;
    right: 0;
    width: 1.0625em;

    background: ${themeVars('colorWhite')};
    border-radius: 50%;

    content: '';

    transition: transform 0.2s ease 0s;
  }
`;

export const StyledCheckbox = ({
  danger = null,
  checkToggle = false,
  decorative = false,
  value = false,
  onChange = undefined,
  disabled = false,
  ...otherProps
}) => {
  const isChecked = () => {
    if (decorative) {
      return true;
    }

    return onChange ? value === true : null;
  };

  return (
    <>
      <HiddenInput
        type="checkbox"
        {...otherProps}
        value={value}
        onChange={onChange}
        disabled={disabled}
        checked={isChecked()}
      />
      {(checkToggle && <StyledToggle danger={danger} />) || (
        <StyledBox disabled={disabled} danger={danger} decorative={decorative}>
          {isChecked() && (
            <svg aria-hidden="true" focusable="false" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path
                d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 
         432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 
         9.997-26.207 9.997-36.204-.001z"
              />
            </svg>
          )}
        </StyledBox>
      )}
    </>
  );
};

StyledCheckbox.propTypes = {
  value: PropTypes.bool,
  danger: PropTypes.bool,
  onChange: PropTypes.func,
  checkToggle: PropTypes.bool,
  disabled: PropTypes.bool,
  decorative: PropTypes.bool,
};

export const StyledCheckboxLabel = styled.span`
  display: block;
  padding-left: 2rem;
  padding-top: ${ifProp({
    smallFont: '0.2rem',
    default: '0',
  })};
  line-height: 1.67;
`;

const StyledRadio = styled.span`
  display: block;
  height: 1.25rem;
  left: 0;
  position: absolute;
  top: 0.2rem;
  width: 1.25rem;

  border: 1px solid ${themeVars('colorDarkDisabled')};
  border-radius: 100%;

  cursor: pointer;

  &::after {
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    transform: scale(0.6667);
    transform-origin: 50% 50%;
    width: 100%;

    background: ${themeVars('colorDarkPrimary')};
    border-radius: 100%;

    content: '';

    transition: opacity 0.2s ease 0s;
  }

  ${ifProp({
    danger: css`
      &::after {
        background: ${themeVars('colorDanger')};
      }
    `,
  })}
`;

const HiddenInput = styled.input`
  left: -9999em;
  position: absolute;

  &:not(:checked) + ${StyledBox}, &:not(:checked) + ${StyledRadio} {
    background-color: ${themeVars('colorBackground')};
    border: 2px solid ${themeVars('colorDarkDisabled')};
    ${ifProp({
      error: css`
        border-color: ${themeVars('colorDanger')};
      `,
    })}

    &::after {
      opacity: 0;
    }
  }

  &:not(:checked) + ${StyledToggle} {
    background-color: ${themeVars('colorDarkDisabled')};
    border-color: ${themeVars('colorDarkDisabled')};

    &::before {
      transform: translateX(-1.1875em);
    }
  }

  &:focus + ${StyledBox}, &:focus + ${StyledRadio} {
    outline: 2px solid ${themeVars('colorPrimary')} !important;
    outline-offset: 1px;
  }
`;

export const StyledRadioButton = ({ danger = null, ...props }) => (
  <>
    <HiddenInput type="radio" {...props} danger={danger} />
    <StyledRadio danger={danger} />
  </>
);

StyledRadioButton.propTypes = {
  danger: PropTypes.string,
};

export const StyledPasswordToggle = styled.button`
  align-items: center;
  display: flex;
  justify-content: center;

  margin: 0;
  padding: 0;
  position: absolute;
  top: 2.1rem;
  right: 1.125em;
  width: 2.4em;

  background: transparent;
  border: none;

  text-align: center;

  cursor: pointer;
`;

export const StyledForm = styled.form`
  margin-bottom: ${ifProp({ marginBottom: '3em' })};
  width: 100%;

  ${props =>
    props.disabled &&
    css`
      position: relative;
      pointer-events: none;
      padding: 10px 20px;

      &:after {
        position: absolute;
        content: '';
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: rgba(0, 0, 0, 0.06);
      }

      input,
      textarea,
      select {
        background: rgba(255, 255, 255, 1);
        opacity: 1;
      }

      div:has(> select)::after,
      button {
        display: none;
      }

      ${StyledRow}:has(> button) {
        padding: 0;
      }

      ${StyledToggle}, ${StyledBox}, ${StyledRadio}::after {
        background: ${themeVars('colorMediumLighterGrey')};
        border-color: ${themeVars('colorMediumLighterGrey')};
      }
    `}
`;

export const StyledFormGroup = styled.fieldset`
  padding: 0 0 1.125rem;
  border: none;
`;
